import React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DealQuestionnaire } from "@/pages/deals/types";
import {
  useUpdateQuestionnaireMutation,
  useRestoreQuestionnaireMutation
} from "@/pages/deals/hooks";
import { getWorkflowTypeDisplay } from "../workflow/helpers";
import { useUsers } from "@/entities/users/hooks";
import { simpleDate } from "@/utils/datetime";
import { Resolve } from "@dreamhub/designsystem/icons";
import {
  getQuestionnaireIcon,
  getBadgeColors,
  getUserDisplayName
} from "./utils";

interface QuestionnaireCardProps {
  questionnaire: DealQuestionnaire;
  stageId?: string;
}

const QuestionnaireCard: React.FC<QuestionnaireCardProps> = ({
  questionnaire,
  stageId
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateQuestionnaireMutation = useUpdateQuestionnaireMutation();
  const restoreQuestionnaireMutation = useRestoreQuestionnaireMutation();

  const {
    name,
    description,
    typeValue,
    lastModifiedAt,
    lastModifiedBy,
    isSystem,
    isDeleted,
    stageName
  } = questionnaire;

  const { users } = useUsers(lastModifiedBy ? [lastModifiedBy] : [], {
    enabled: !!lastModifiedBy
  });

  const getEditedByText = (): string => {
    if (!lastModifiedBy || !lastModifiedAt) return "";

    const userName = getUserDisplayName(lastModifiedBy, users);
    const formattedDate = simpleDate(new Date(lastModifiedAt), "short", false);

    return t("workflows.status.editedBy", { userName, date: formattedDate });
  };

  const handleAddToStage = async () => {
    if (!questionnaire.id || !stageId) {
      console.error("Missing questionnaire ID or stage ID");
      return;
    }

    try {
      await restoreQuestionnaireMutation.mutateAsync({
        pathParams: { questionnaireId: questionnaire.id }
      });
      await updateQuestionnaireMutation.mutateAsync({
        pathParams: { questionnaireId: questionnaire.id },
        data: {
          stageId: parseInt(stageId)
        }
      });
      queryClient.invalidateQueries({
        queryKey: ["settings/questionnaireAll"]
      });
      queryClient.invalidateQueries({
        queryKey: ["settings/deals/stages/stageById"]
      });
    } catch (error) {
      console.error("Failed to add questionnaire to stage:", error);
    }
  };

  const showAddToStageButton = isSystem && isDeleted;

  return (
    <div className="flex h-[152px] p-4 justify-center items-center gap-4 rounded-2xl border border-[#2B374A] bg-[#1C2432]">
      <div className="flex-shrink-0">{getQuestionnaireIcon(typeValue)}</div>
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-2 h-[68px]">
          <Text variant="lgLabel">{name}</Text>
          <Text variant="bodySecondary">{description}</Text>
        </div>

        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-2">
            <Badge
              text={getWorkflowTypeDisplay(typeValue, t)}
              textVariant="body"
              className={getBadgeColors(typeValue)}
            />
            {stageName && (
              <Badge
                text={`${t("usedIn")} ${stageName}`}
                textVariant="body"
                textClassName="text-primary"
                className="bg-badges-filled border-border-badge-selected px-[6px] py-1"
                icon={<Resolve />}
              />
            )}
            <Text variant="smBodyTertiary">{getEditedByText()}</Text>
          </div>

          <div className="flex items-center gap-2">
            {!showAddToStageButton && (
              <Button variant="secondary" disabled>
                {t("inUse")}
              </Button>
            )}
            {showAddToStageButton && (
              <Button variant="primary" onClick={handleAddToStage}>
                {t("addToStage")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireCard;
